import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  TablePagination,
  Stack,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const BLUE = "#1565C0";
const BLUE_DARK = "#0D47A1";
const BLUE_HOVER = "#0b3c8a";
const TEXT = "#0f172a";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";
const SOFT = "#f8fafc";
const COLORS = [BLUE_DARK, BLUE, BLUE_HOVER, "#4f83cc", "#90caf9", "#bbdefb"];

const money = (value) => {
  const n = Number(value || 0);
  return `$${n.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const qty = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatShortDate = (raw) => {
  if (!raw) return "";

  const value = String(raw);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
  });
};

const formatMonth = (raw) => {
  if (!raw) return "";

  const value = String(raw);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("es-EC", {
    month: "short",
  });
};

const Panel = ({ title, subtitle, children, minHeight }) => {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
        bgcolor: "#fff",
        height: "100%",
        minHeight: minHeight || "auto",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 19, color: TEXT }}>
            {title}
          </Typography>

          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.4 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {children}
    </Paper>
  );
};

const EmptyState = ({ text = "Sin información suficiente para mostrar." }) => {
  return (
    <Box
      sx={{
        height: 240,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#94a3b8",
        fontSize: 14,
        fontWeight: 700,
        textAlign: "center",
      }}
    >
      {text}
    </Box>
  );
};

const LoadingBox = () => {
  return (
    <Box
      sx={{
        height: 240,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={30} />
    </Box>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <Paper
      sx={{
        p: 1.2,
        borderRadius: 2,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 10px 30px rgba(15,23,42,0.14)",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: TEXT, mb: 0.7 }}>
        {label}
      </Typography>

      {payload.map((item) => (
        <Typography
          key={item.dataKey}
          sx={{ fontSize: 12, color: "#475569", fontWeight: 700 }}
        >
          {item.name}: {money(item.value)}
        </Typography>
      ))}
    </Paper>
  );
};

const DashboardTable = ({ columns, rows, emptyText, rowsPerPageDefault = 5 }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageDefault);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    return (rows || []).slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  if (!rows?.length) {
    return <EmptyState text={emptyText} />;
  }

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{
                    bgcolor: BLUE,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    borderBottom: `1px solid ${BLUE_DARK}`,
                    whiteSpace: "nowrap",
                    py: 1.15,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={row.id || row.persona_id || row.product_id || row.codigo || index} hover>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      fontSize: 12.5,
                      fontWeight: col.bold ? 700 : 500,
                      color: col.muted ? MUTED : "#334155",
                      borderBottom: "1px solid #f1f5f9",
                      maxWidth: col.maxWidth || 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      py: 1.15,
                    }}
                  >
                    {col.render ? col.render(row, index) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={(rows || []).length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          borderTop: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          "& .MuiTablePagination-toolbar": {
            px: 1,
            minHeight: 48,
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: 12.5,
            color: MUTED,
            fontWeight: 600,
          },
          "& .MuiTablePagination-select": {
            fontSize: 12.5,
            fontWeight: 700,
            color: TEXT,
          },
          "& .MuiIconButton-root": {
            color: BLUE,
          },
          "& .MuiIconButton-root:hover": {
            bgcolor: "rgba(21,101,192,0.08)",
          },
          "& .MuiIconButton-root.Mui-disabled": {
            color: "#cbd5e1",
          },
        }}
      />
    </Box>
  );
};

const RatioLine = ({ label, value, total }) => {
  const safeValue = Math.abs(Number(value || 0));
  const safeTotal = Math.max(Math.abs(Number(total || 0)), 1);
  const progress = Math.max(0, Math.min(100, (safeValue / safeTotal) * 100));

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.7 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: TEXT }}>
          {money(value)}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 99,
          bgcolor: "#e2e8f0",
          "& .MuiLinearProgress-bar": {
            bgcolor: BLUE,
            borderRadius: 99,
          },
        }}
      />
    </Box>
  );
};

const MiniCard = ({ title, value }) => {
  return (
    <Box
      sx={{
        p: 1.6,
        borderRadius: 3,
        bgcolor: SOFT,
        border: `1px solid ${BORDER}`,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: TEXT }}>
        {value}
      </Typography>
    </Box>
  );
};

const FinancialOverview = ({ data, loading }) => {
  const resumen = data?.resumen || {};
  const graficas = data?.graficas || {};
  const clientes = data?.clientes || {};
  const proveedores = data?.proveedores || {};
  const productos = data?.productos || {};
  const inventario = data?.inventario || {};
  const finanzas = data?.finanzas || {};

  const ventasCompras = useMemo(() => {
    return (graficas.ventas_compras_resultado || []).map((item) => ({
      ...item,
      label: data?.periodo?.tipo === "anio" ? formatMonth(item.fecha) : formatShortDate(item.fecha),
      ventas: Number(item.ventas || 0),
      compras: Number(item.compras || 0),
      resultado: Number(item.resultado || 0),
    }));
  }, [graficas.ventas_compras_resultado, data?.periodo?.tipo]);

  const estadoFinanciero = useMemo(() => {
    return (finanzas.estados_financieros || [])
      .map((item) => ({
        name: item.grupo,
        value: Math.abs(Number(item.total || 0)),
        total: Number(item.total || 0),
      }))
      .filter((item) => item.value > 0);
  }, [finanzas.estados_financieros]);

  const topClientesChart = useMemo(() => {
    return (clientes.mejores || []).slice(0, 6).map((item) => ({
      nombre: String(item.nombre || "Cliente").slice(0, 18),
      total: Number(item.total || 0),
    }));
  }, [clientes.mejores]);

  const totalEstructura = estadoFinanciero.reduce((acc, item) => acc + Number(item.value || 0), 0);

  if (loading) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Panel title="Cargando análisis" subtitle="Preparando información financiera, cartera e inventario">
            <LoadingBox />
          </Panel>
        </Grid>
      </Grid>
    );
  }

  if (!data) {
    return (
      <Panel title="Sin datos" subtitle="No se pudo cargar la información del dashboard">
        <EmptyState />
      </Panel>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={8}>
        <Panel
          title="Ventas, compras y resultado"
          subtitle="Comparación del movimiento comercial del período seleccionado"
          minHeight={390}
        >
          {ventasCompras.length ? (
            <Box sx={{ width: "100%", height: 310 }}>
              <ResponsiveContainer>
                <AreaChart data={ventasCompras} margin={{ top: 10, right: 18, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="ventas" name="Ventas" stroke={BLUE} fill={BLUE} fillOpacity={0.16} strokeWidth={2.6} />
                  <Area type="monotone" dataKey="compras" name="Compras" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.16} strokeWidth={2.3} />
                  <Area type="monotone" dataKey="resultado" name="Resultado" stroke={TEXT} fill={TEXT} fillOpacity={0.08} strokeWidth={2.2} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <EmptyState text="Todavía no hay ventas o compras en este período." />
          )}
        </Panel>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Panel
          title="Estructura financiera"
          subtitle="Lectura resumida por grupo contable"
          minHeight={390}
        >
          {estadoFinanciero.length ? (
            <>
              <Box sx={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={estadoFinanciero}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={58}
                      outerRadius={88}
                      paddingAngle={2}
                    >
                      {estadoFinanciero.map((_, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => money(value)} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {estadoFinanciero.slice(0, 5).map((item) => (
                <RatioLine
                  key={item.name}
                  label={item.name}
                  value={item.total}
                  total={totalEstructura}
                />
              ))}
            </>
          ) : (
            <EmptyState text="No hay saldos contables suficientes para graficar." />
          )}
        </Panel>
      </Grid>

      <Grid item xs={12} md={6}>
        <Panel
          title="Clientes que más compran"
          subtitle="Ranking por ventas autorizadas del período"
          minHeight={390}
        >
          {topClientesChart.length ? (
            <Box sx={{ width: "100%", height: 310 }}>
              <ResponsiveContainer>
                <BarChart data={topClientesChart} layout="vertical" margin={{ top: 6, right: 18, left: 24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="nombre" type="category" width={110} tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" name="Total" fill={BLUE} radius={[0, 8, 8, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <EmptyState text="No hay clientes con ventas en este período." />
          )}
        </Panel>
      </Grid>

      <Grid item xs={12} md={6}>
        <Panel
          title="Contexto de cartera"
          subtitle="Dinero pendiente por cobrar y obligaciones por pagar"
          minHeight={390}
        >
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <RatioLine
              label="Cuentas por cobrar"
              value={resumen.cuentas_por_cobrar}
              total={Number(resumen.cuentas_por_cobrar || 0) + Number(resumen.cuentas_por_pagar || 0)}
            />
            <RatioLine
              label="Cuentas por pagar"
              value={resumen.cuentas_por_pagar}
              total={Number(resumen.cuentas_por_cobrar || 0) + Number(resumen.cuentas_por_pagar || 0)}
            />
            <RatioLine
              label="Cobros del período"
              value={resumen.cobros_periodo}
              total={Number(resumen.cobros_periodo || 0) + Number(resumen.pagos_periodo || 0)}
            />
            <RatioLine
              label="Pagos del período"
              value={resumen.pagos_periodo}
              total={Number(resumen.cobros_periodo || 0) + Number(resumen.pagos_periodo || 0)}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <MiniCard title="Stock bajo" value={Number(resumen.productos_stock_bajo || 0)} />
            </Grid>

            <Grid item xs={6}>
              <MiniCard title="Sin stock" value={Number(resumen.productos_sin_stock || 0)} />
            </Grid>

            <Grid item xs={6}>
              <MiniCard title="Proformas" value={Number(resumen.proformas_total || 0)} />
            </Grid>

            <Grid item xs={6}>
              <MiniCard title="Conversión" value={`${Number(resumen.conversion_proformas || 0).toFixed(1)}%`} />
            </Grid>
          </Grid>
        </Panel>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Panel
          title="Mejores proveedores"
          subtitle="Proveedores con mayor volumen de compras"
        >
          <DashboardTable
            emptyText="No hay compras a proveedores en este período."
            rows={proveedores.mejores || []}
            columns={[
              {
                key: "nombre",
                label: "Proveedor",
                bold: true,
                maxWidth: 220,
              },
              {
                key: "documentos",
                label: "Docs",
                align: "right",
              },
              {
                key: "total",
                label: "Comprado",
                align: "right",
                render: (row) => money(row.total),
              },
              {
                key: "saldo",
                label: "Pendiente",
                align: "right",
                render: (row) => money(row.saldo),
              },
            ]}
          />
        </Panel>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Panel
          title="Clientes con mayor deuda"
          subtitle="Clientes que más afectan la cartera por cobrar"
        >
          <DashboardTable
            emptyText="No existen saldos pendientes de clientes."
            rows={clientes.mayor_deuda || []}
            columns={[
              {
                key: "nombre",
                label: "Cliente",
                bold: true,
                maxWidth: 220,
              },
              {
                key: "deuda",
                label: "Deuda",
                align: "right",
                render: (row) => money(row.deuda),
              },
              {
                key: "credito",
                label: "Crédito",
                align: "right",
                render: (row) => money(row.credito),
              },
              {
                key: "saldo_neto",
                label: "Saldo",
                align: "right",
                render: (row) => money(row.saldo_neto),
              },
            ]}
          />
        </Panel>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Panel
          title="Productos más vendidos"
          subtitle="Productos que más mueven ingresos en el período"
        >
          <DashboardTable
            emptyText="No hay productos vendidos en este período."
            rows={productos.mas_vendidos || []}
            columns={[
              {
                key: "codigo",
                label: "Código",
                bold: true,
                maxWidth: 110,
              },
              {
                key: "descripcion",
                label: "Producto",
                maxWidth: 260,
              },
              {
                key: "cantidad",
                label: "Cant.",
                align: "right",
                render: (row) => qty(row.cantidad),
              },
              {
                key: "total",
                label: "Vendido",
                align: "right",
                render: (row) => money(row.total),
              },
            ]}
          />
        </Panel>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Panel
          title="Stock crítico"
          subtitle="Productos que necesitan revisión de inventario"
        >
          <DashboardTable
            emptyText="No hay productos en stock crítico."
            rows={productos.stock_critico || []}
            columns={[
              {
                key: "codigo",
                label: "Código",
                bold: true,
                maxWidth: 110,
              },
              {
                key: "nombre",
                label: "Producto",
                maxWidth: 260,
              },
              {
                key: "stock",
                label: "Stock",
                align: "right",
                render: (row) => qty(row.stock),
              },
              {
                key: "stock_minimo",
                label: "Mínimo",
                align: "right",
                render: (row) => qty(row.stock_minimo),
              },
            ]}
          />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <Panel
          title="Caja, inventario y operación"
          subtitle="Resumen complementario para entender la presión operativa del período"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <MiniCard title="Cobros" value={money(resumen.cobros_periodo)} />
            </Grid>

            <Grid item xs={12} md={3}>
              <MiniCard title="Pagos" value={money(resumen.pagos_periodo)} />
            </Grid>

            <Grid item xs={12} md={3}>
              <MiniCard title="Ingresos inventario" value={qty(inventario?.cantidades?.ingresos)} />
            </Grid>

            <Grid item xs={12} md={3}>
              <MiniCard title="Egresos inventario" value={qty(inventario?.cantidades?.egresos)} />
            </Grid>
          </Grid>
        </Panel>
      </Grid>
    </Grid>
  );
};

export default FinancialOverview;